import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import FilterDropdown, {FilterDropdownRef} from './FilterDropdown';
import FilterLayout from './FilterLayout';
import FilterMultiSelect from './FilterMultiSelect';
import {FilterOption} from '../../../common/types/FilterOption';
import collectionsService from '../../services/collectionsService';
import {handleError} from '../../../common/utils/errorHandling';
import './filters.scss';
import {getValueWithFallback} from '../../../common/types/StringTranslations';

interface CollectionsFilterProps {
  collections?: string[];
  onFilterChange: (collections?: string[]) => void;
}

const CollectionsFilter: React.FC<CollectionsFilterProps> = ({
  collections = [],
  onFilterChange
}) => {
  const {t, i18n} = useTranslation();
  const [tempCollections, setTempCollections] = useState<string[]>(collections);
  const [collectionOptions, setCollectionOptions] = useState<FilterOption[]>([]);
  const filterDropdownRef = useRef<FilterDropdownRef>(null);

  const hasActiveFilter = collections.length > 0;

  useEffect(() => {
    fetchCollections();
  }, []);

  const fetchCollections = async () => {
    handleError(async () => {
      const collectionsData = await collectionsService.getAllCollections();
      const options: FilterOption[] = collectionsData.map(collection => ({
        label: getValueWithFallback(collection.title, i18n.language),
        value: collection.id
      }));
      setCollectionOptions(options);
    }, 'Error fetching collections');
  };

  const applyFilter = () => {
    const filteredCollections = tempCollections.length > 0 ? tempCollections : undefined;
    onFilterChange(filteredCollections);
  };

  const clearFilter = () => {
    setTempCollections([]);
    onFilterChange(undefined);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      applyFilter();
      filterDropdownRef.current?.close();
    } else if (event.key === 'Escape') {
      setTempCollections(collections);
      filterDropdownRef.current?.close();
    }
  };

  const handleApplyClick = () => {
    applyFilter();
    filterDropdownRef.current?.close();
  };

  const getDisplayLabel = () => {
    if (collections.length === 0) {
      return t('gallery.filters.collections');
    }
    return `${t('gallery.filters.collections')} | ${collections.length}`;
  };

  return (
    <FilterDropdown
      ref={filterDropdownRef}
      label={getDisplayLabel()}
      hasActiveFilter={hasActiveFilter}
      onClearFilter={clearFilter}
    >
      <FilterLayout
        title={t('gallery.filters.filterByCollections')}
        onClear={clearFilter}
        onApply={handleApplyClick}
      >
        <FilterMultiSelect
          value={tempCollections}
          options={collectionOptions}
          onChange={setTempCollections}
          onKeyDown={handleKeyPress}
          width={600}
        />
      </FilterLayout>
    </FilterDropdown>
  );
};

export default CollectionsFilter;

